import React, { Suspense, useEffect } from "react";
import { Capacitor } from "@capacitor/core";
import { Route, Routes, useLocation } from "react-router-dom";
import appStyle from "./styles/App.module.css";
import NavBarComponent from "./components/NavBarComponent";
import DesktopSidebar from "./components/DesktopSidebar";
// Ad/upgrade banners removed — upgrade prompts are now inline via UpgradeNudge
import useFeatureFlag from "./hooks/useFeatureFlag";
import { useUser } from "./contexts/UserContext";
import NotificationToast from "./components/NotificationToast";
import SecondaryTopBar from "./components/SecondaryTopBar";
import useTaskNotifications from "./hooks/useTaskNotifications";
import useEngagementNotifications from "./hooks/useEngagementNotifications";
import usePushNotifications from "./hooks/usePushNotifications";
import useSocialNotifications from "./hooks/useSocialNotifications";
import useCycleNotifications from "./hooks/useCycleNotifications";
import ProtectedRoute from "./components/ProtectedRoute";
import BadgeUnlockNotifier from "./components/BadgeUnlockNotifier";
import FocusGuard from "./components/FocusGuard";
import UpdateBanner from "./components/UpdateBanner";
import NativeAppUpdateHandler from "./components/NativeAppUpdateHandler";
import AccountSwitchedNotice from "./components/AccountSwitchedNotice";
import { useTheme } from "./contexts/ThemeSelection";
import { TopBarActionsProvider } from "./contexts/TopBarActionsContext";
import { captureInstallSource } from "./utils/installSource";

// Lazy-loaded page components
const HomePage = React.lazy(() => import("./pages/HomePage"));
const NoteListPage = React.lazy(() => import("./pages/notes/NoteListPage"));
const NoteDetailPage = React.lazy(() => import("./pages/notes/NoteDetailPage"));
const NoteCreate = React.lazy(() => import("./pages/notes/NoteCreate"));
const ListPage = React.lazy(() => import("./pages/lists/ListPage"));
const ListDetailPage = React.lazy(() => import("./pages/lists/ListDetailPage"));
const ListCreate = React.lazy(() => import("./pages/lists/ListCreate"));
const TaskPage = React.lazy(() => import("./pages/tasks/TaskPage"));
const AccountPage = React.lazy(() => import("./pages/account/AccountPage"));
const SignUp = React.lazy(() => import("./pages/auth/SignUp"));
const SignIn = React.lazy(() => import("./pages/auth/SignIn"));
const CalendarPage = React.lazy(() => import("./pages/calendar/CalendarPage"));
const HealthPage = React.lazy(() => import("./pages/health/HealthPage"));
const InsightsPage = React.lazy(() => import("./pages/health/InsightsPage"));
const SubscriptionSuccess = React.lazy(() => import("./pages/account/SubscriptionSuccess"));
const AdminPage = React.lazy(() => import("./pages/account/AdminPage"));
const FamilyPage = React.lazy(() => import("./pages/family/FamilyPage"));
const FitnessPage = React.lazy(() => import("./pages/fitness/FitnessPage"));
const ChallengesPage = React.lazy(() => import("./pages/fitness/ChallengesPage"));
const RunningPage = React.lazy(() => import("./pages/fitness/RunningPage"));
const WomensHealthPage = React.lazy(() => import("./pages/health/WomensHealthPage"));
const CycleImportPage = React.lazy(() => import("./pages/health/CycleImportPage"));
const CommunityPage = React.lazy(() => import("./pages/community/CommunityPage"));
const NotFoundPage = React.lazy(() => import("./pages/NotFoundPage"));
const PrivacyPolicyPage = React.lazy(() => import("./pages/PrivacyPolicyPage"));
const DeleteAccountPage = React.lazy(() => import("./pages/DeleteAccountPage"));
const TestLoginPage = React.lazy(() => import("./pages/TestLoginPage"));
const FocusPickerPage = React.lazy(() => import("./pages/FocusPickerPage"));
const CycleShareView = React.lazy(() => import("./components/CycleShareView"));
const PregnancyShareView = React.lazy(() => import("./components/PregnancyShareView"));
const WorkoutShareView = React.lazy(() => import("./components/WorkoutShareView"));
const TipUsPage = React.lazy(() => import("./pages/TipUsPage"));
const ReferralDashboard = React.lazy(() => import("./pages/account/ReferralDashboard"));
const BadgesPage = React.lazy(() => import("./pages/badges/BadgesPage"));
const PetPage = React.lazy(() => import("./pages/PetPage"));
const CollectionPage = React.lazy(() => import("./pages/collection/CollectionPage"));

const LoadingFallback: React.FC = () => {
  const { activeTheme, theme } = useTheme();
  const tc = theme[activeTheme];
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        minHeight: "60vh",
        color: tc.color,
        backgroundColor: tc.backgroundColor,
        fontSize: "1.1rem",
      }}
    >
      Loading…
    </div>
  );
};

const ONYXLIFE_SOURCE_KEY = "onyxlife_source";

const App: React.FC = () => {
  // Initialize native Google Auth plugin
  useEffect(() => {
    if (Capacitor.isNativePlatform()) {
      import("@codetrix-studio/capacitor-google-auth").then(({ GoogleAuth }) => {
        GoogleAuth.initialize({
          clientId: "305275227699-bved5a9rsdu8lon55sr73p7ib9mrperk.apps.googleusercontent.com",
          scopes: ["profile", "email"],
          grantOfflineAccess: false,
        });
      }).catch(() => { /* plugin not available */ });
    }
  }, []);

  // Capture ?src= param from any URL and persist to localStorage
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const src = params.get("src");
    if (src) {
      localStorage.setItem(ONYXLIFE_SOURCE_KEY, src.toLowerCase());
    }
  }, []);

  // Capture UTM params + referrer + platform on first load so we can attribute
  // new accounts to their traffic source. Idempotent — safe to call every mount.
  useEffect(() => {
    captureInstallSource();
  }, []);

  const referralsEnabled = useFeatureFlag("referrals");
  const virtualPetEnabled = useFeatureFlag("virtual_pet");
  const collectionEnabled = useFeatureFlag("collection");
  const { userDetails } = useUser()!;
  const isLoggedIn = !!userDetails?.user;
  // Show nav + top bar as soon as the user is signed in. The /pick-focus
  // route is a full-screen welcome flow, so we hide chrome there to keep the
  // first-run experience clean.
  const location = useLocation();
  const isOnPickFocus = location.pathname === "/pick-focus";
  const showChrome = isLoggedIn && !isOnPickFocus;
  useTaskNotifications();
  useEngagementNotifications();
  usePushNotifications();
  useSocialNotifications();
  useCycleNotifications();
  return (
    <TopBarActionsProvider>
    <div id="theme" className={appStyle.Main}>
      {showChrome && <DesktopSidebar />}
      {showChrome && <NavBarComponent />}
      {showChrome && <SecondaryTopBar />}
      {showChrome && <NotificationToast />}
      {isLoggedIn && <BadgeUnlockNotifier />}
      {isLoggedIn && <UpdateBanner />}
      {isLoggedIn && <NativeAppUpdateHandler />}
      {isLoggedIn && <AccountSwitchedNotice />}
      <Suspense fallback={<LoadingFallback />}>
        <Routes>
          {/* Public routes */}
          <Route path="/" element={<HomePage />} />
          <Route path="/sign-up" element={<SignUp />} />
          <Route path="/sign-in" element={<SignIn />} />
          <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
          <Route path="/delete-account" element={<DeleteAccountPage />} />
          <Route path="/test-login" element={<TestLoginPage />} />
          <Route path="/cycle-share/:code" element={<CycleShareView />} />
          <Route path="/pregnancy-share/:code" element={<PregnancyShareView />} />
          <Route path="/workout-share/:code" element={<WorkoutShareView />} />

          {/* Protected routes — redirect to /sign-in if not logged in */}
          <Route element={<ProtectedRoute />}>
            <Route path="/account" element={<AccountPage />} />
            <Route path="/notes" element={<FocusGuard><NoteListPage /></FocusGuard>} />
            <Route path="/notes/note/:docId" element={<FocusGuard><NoteDetailPage /></FocusGuard>} />
            <Route path="/notes/note/create" element={<FocusGuard><NoteCreate /></FocusGuard>} />
            <Route path="/tasks/" element={<FocusGuard><TaskPage /></FocusGuard>} />
            <Route path="/lists/" element={<FocusGuard><ListPage /></FocusGuard>} />
            <Route path="/lists/list/:docId" element={<FocusGuard><ListDetailPage /></FocusGuard>} />
            <Route path="/lists/list/create" element={<FocusGuard><ListCreate /></FocusGuard>} />
            <Route path="/calendar" element={<FocusGuard><CalendarPage /></FocusGuard>} />
            <Route path="/health" element={<FocusGuard><HealthPage /></FocusGuard>} />
            <Route path="/health/insights" element={<FocusGuard><InsightsPage /></FocusGuard>} />
            <Route path="/womens-health" element={<FocusGuard><WomensHealthPage /></FocusGuard>} />
            <Route path="/cycle-import" element={<FocusGuard><CycleImportPage /></FocusGuard>} />
            <Route path="/fitness" element={<FocusGuard><FitnessPage /></FocusGuard>} />
            <Route path="/challenges" element={<FocusGuard><ChallengesPage /></FocusGuard>} />
            <Route path="/running" element={<FocusGuard><RunningPage /></FocusGuard>} />
            <Route path="/family" element={<FocusGuard><FamilyPage /></FocusGuard>} />
            <Route path="/community" element={<FocusGuard><CommunityPage /></FocusGuard>} />
            {virtualPetEnabled && <Route path="/pet" element={<FocusGuard><PetPage /></FocusGuard>} />}
            {collectionEnabled && <Route path="/collection" element={<FocusGuard><CollectionPage /></FocusGuard>} />}
            <Route path="/badges" element={<BadgesPage />} />
            <Route path="/pick-focus" element={<FocusPickerPage />} />
            <Route path="/admin" element={<AdminPage />} />
            <Route path="/tip-us" element={<TipUsPage />} />
            {referralsEnabled && <Route path="/referrals" element={<ReferralDashboard />} />}
            <Route path="/subscriptionSuccess" element={<SubscriptionSuccess />} />
          </Route>

          {/* Catch-all 404 */}
          <Route path="*" element={<NotFoundPage />} />
        </Routes>
      </Suspense>
    </div>
    </TopBarActionsProvider>
  );
};

export default App;
